/**
 * Knack before / after comparison slider.
 *
 * Drag (pointer or touch) or keyboard the handle to reveal the "after" image over the "before".
 * Sets --knack-ba-pos (a % from the left edge) which both clips the after layer and positions the
 * handle. Progressive enhancement: with no JS the CSS shows the configured start position and the
 * handle stays a focusable button. Keyboard: Left/Down and Right/Up nudge (Shift = bigger step),
 * Home/End jump to the ends. Pointer events cover mouse, touch and pen.
 */
class KnackBeforeAfter extends HTMLElement {
  connectedCallback() {
    this.frame = this.querySelector('[data-knack-ba-frame]');
    this.handle = this.querySelector('[data-knack-ba-handle]');
    if (!this.frame || !this.handle) return;

    this.onDown = this.onDown.bind(this);
    this.onMove = this.onMove.bind(this);
    this.onUp = this.onUp.bind(this);
    this.onKey = this.onKey.bind(this);

    // Sync the JS position with the start value the CSS already rendered.
    const start = parseFloat(this.handle.getAttribute('aria-valuenow'));
    this.setPos(Number.isFinite(start) ? start : 50);

    this.frame.addEventListener('pointerdown', this.onDown);
    this.handle.addEventListener('keydown', this.onKey);
  }

  disconnectedCallback() {
    this.frame?.removeEventListener('pointerdown', this.onDown);
    window.removeEventListener('pointermove', this.onMove);
    window.removeEventListener('pointerup', this.onUp);
  }

  setPos(pct) {
    const clamped = Math.max(0, Math.min(100, pct));
    this.pos = clamped;
    this.style.setProperty('--knack-ba-pos', clamped + '%');
    this.handle.setAttribute('aria-valuenow', Math.round(clamped));
  }

  posFromEvent(event) {
    const rect = this.frame.getBoundingClientRect();
    if (!rect.width) return this.pos;
    return ((event.clientX - rect.left) / rect.width) * 100;
  }

  onDown(event) {
    if (event.button != null && event.button !== 0) return;
    event.preventDefault();
    this.dragging = true;
    this.handle.focus();
    this.setPos(this.posFromEvent(event));
    window.addEventListener('pointermove', this.onMove);
    window.addEventListener('pointerup', this.onUp);
  }

  onMove(event) {
    if (this.dragging) this.setPos(this.posFromEvent(event));
  }

  onUp() {
    this.dragging = false;
    window.removeEventListener('pointermove', this.onMove);
    window.removeEventListener('pointerup', this.onUp);
  }

  onKey(event) {
    const step = event.shiftKey ? 10 : 2;
    let next = this.pos;
    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        next = this.pos - step;
        break;
      case 'ArrowRight':
      case 'ArrowUp':
        next = this.pos + step;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = 100;
        break;
      default:
        return;
    }
    event.preventDefault();
    this.setPos(next);
  }
}

if (!customElements.get('knack-before-after')) {
  customElements.define('knack-before-after', KnackBeforeAfter);
}
